"use client";

import dynamic from "next/dynamic";
import { useParams } from "next/navigation";
import { FC, ReactNode, createContext, useEffect, useState } from "react";

import useIsSaleScreen from "@/hooks/useIsSaleScreen";

const CreateLeadModal = dynamic(() => import("@/components/CreateLeadModal/CreateLeadModal"), { ssr: false });
const CreateNpsLeadModal = dynamic(() => import("@/components/NpsModel/NpsIframe"), { ssr: false });
const CreateScheduleCallModal = dynamic(() => import("@/components/CreateScheduleCallModal/CreateScheduleCallModal"), { ssr: false });
const CreateGenericRequestModal = dynamic(() => import("@/components/CreateGenericRequestModal/CreateGenericRequestModal"), { ssr: false });
const BookVisitModal = dynamic(() => import("@/components/BookVisitModal/BookVisitModal"), { ssr: false });
const InnovationLeadModal = dynamic(() => import("@/components/InnovationLeadModal/InnovationLeadModal"), { ssr: false });
const SoftLoginModal = dynamic(() => import("@/components/SoftLogin/SoftLoginModal"), { ssr: false });

interface Props {
  settings: { [key: string]: any };
  children: ReactNode;
}
interface ModalsContextProps {
  setGetInTouchModalOpen: (open: boolean) => void;
  setinnovationLeadModalOpen: (open: boolean) => void;
  setGenericRequestModalOpen: (open: boolean) => void;
  setBookVisitModalOpen: (open: boolean) => void;
  setEnquireModalOpen: (open: boolean) => void;
  setScheduleCallModalOpen: (open: boolean) => void;
  setNpsModalOpen: (open: boolean) => void;
  setDownloadBrochureModalOpen: (open: boolean) => void;
  setWebImmersiveLead: (open: boolean) => void;
  setNotifyMeModalOpen: (open: boolean) => void;
  settings: { [key: string]: any };
  additionalInfo: Record<string, any>;
  setAdditionalInfo: (updated: Record<string, any>) => void;
  setMetaverseWalkthroughModalOpen: (open: boolean) => void;
  setGetFormPageLocation: (text: string | undefined) => void;
  getFormPageLocation: string | undefined;
}
export const ModalsContext = createContext<ModalsContextProps>({
  setGetInTouchModalOpen: () => {},
  setinnovationLeadModalOpen: () => {},
  setGenericRequestModalOpen: () => {},
  setBookVisitModalOpen: () => {},
  setScheduleCallModalOpen: () => {},
  setEnquireModalOpen: () => {},
  setDownloadBrochureModalOpen: () => {},
  setNotifyMeModalOpen: () => {},
  setWebImmersiveLead: () => {},
  settings: {},
  additionalInfo: {},
  setAdditionalInfo: () => {},
  setMetaverseWalkthroughModalOpen: () => {},
  setGetFormPageLocation: () => {},
  setNpsModalOpen: () => {},
  getFormPageLocation: "",
});

const ArticleTypeList = ["blog", "news", "industry-news", "press-releases"];

const ModalsContextProvider: FC<Props> = ({ children, settings }) => {
  const [getInTouchModalOpen, setGetInTouchModalOpen] = useState<boolean>(false);
  const [innovationLeadModalOpen, setinnovationLeadModalOpen] = useState<boolean>(false);
  const [genericRequestModalOpen, setGenericRequestModalOpen] = useState<boolean>(false);
  const [scheduleCallModalOpen, setScheduleCallModalOpen] = useState<boolean>(false);
  const [bookVisitModalOpen, setBookVisitModalOpen] = useState<boolean>(false);
  const [enquireModalOpen, setEnquireModalOpen] = useState<boolean>(false);
  const [npsModalOpen, setNpsModalOpen] = useState<boolean>(false);
  // const [shownpsModal, setShowNpsModal] = useState<boolean>(false);
  const [downloadBrochureModalOpen, setDownloadBrochureModalOpen] = useState<boolean>(false);
  const [notifyMeModalOpen, setNotifyMeModalOpen] = useState<boolean>(false);
  const [additionalInfo, setAdditionalInfo] = useState<Record<string, any>>({});
  const [webImmersiveLead, setWebImmersiveLead] = useState(false);
  const [metaverseWalkthroughModalOpen, setMetaverseWalkthroughModalOpen] = useState<boolean>(false);
  const [getFormPageLocation, setGetFormPageLocation] = useState<string | undefined>("");
  const params = useParams<any>();

  const isSo = useIsSaleScreen();

  let modeForEnquireModal =
    params?.slug?.[0] === "mortgage-calculator"
      ? "mortgageEnquire"
      : ArticleTypeList.includes(params?.articleType)
        ? "blogEnquireModalForTitle"
        : "enquire";

  useEffect(() => {}, [additionalInfo]);

  // useEffect(() => {
  //   let timer: NodeJS.Timeout;
  //   if (npsModalOpen) {
  //     timer = setTimeout(() => {
  //       setShowNpsModal(true);
  //     }, 10000);
  //   } else {
  //     setShowNpsModal(false);
  //   }
  //   return () => clearTimeout(timer);
  // }, [npsModalOpen]);

  return (
    <ModalsContext.Provider
      value={{
        setGetInTouchModalOpen,
        setinnovationLeadModalOpen,
        setGenericRequestModalOpen,
        setBookVisitModalOpen,
        setEnquireModalOpen,
        setDownloadBrochureModalOpen,
        setNotifyMeModalOpen,
        settings,
        additionalInfo,
        setAdditionalInfo,
        setWebImmersiveLead,
        setMetaverseWalkthroughModalOpen,
        setGetFormPageLocation,
        setNpsModalOpen,
        setScheduleCallModalOpen,
        getFormPageLocation,
      }}
    >
      {children}
      {getInTouchModalOpen && (
        <CreateLeadModal
          mode={webImmersiveLead ? "webImmersiveLead" : "getInTouch"}
          closeModal={() => setGetInTouchModalOpen(false)}
          leadFormPageLocation={getFormPageLocation}
        />
      )}
      {scheduleCallModalOpen && (
        <CreateScheduleCallModal mode="scheduleCall" closeModal={() => setScheduleCallModalOpen(false)} leadFormPageLocation={getFormPageLocation} />
      )}
      {genericRequestModalOpen && (
        <CreateGenericRequestModal closeModal={() => setGenericRequestModalOpen(false)} leadFormPageLocation={getFormPageLocation} />
      )}
      {bookVisitModalOpen && (
        <BookVisitModal open={bookVisitModalOpen} closeModal={() => setBookVisitModalOpen(false)} leadFormPageLocation={getFormPageLocation} />
      )}
      {enquireModalOpen && (
        <CreateLeadModal
          mode={modeForEnquireModal}
          closeModal={() => {
            setEnquireModalOpen(false);
          }}
          leadFormPageLocation={getFormPageLocation}
        />
      )}
      {downloadBrochureModalOpen && (
        <CreateLeadModal mode="downloadBrochure" closeModal={() => setDownloadBrochureModalOpen(false)} leadFormPageLocation={getFormPageLocation} />
      )}
      {notifyMeModalOpen && (
        <CreateLeadModal mode="notifyMe" closeModal={() => setNotifyMeModalOpen(false)} leadFormPageLocation={getFormPageLocation} />
      )}
      {npsModalOpen && <CreateNpsLeadModal mode="nps" isOpen={npsModalOpen} onClose={() => setNpsModalOpen(false)} />}
      <SoftLoginModal />
      {metaverseWalkthroughModalOpen && <CreateLeadModal mode="metaverseWalkthrough" closeModal={() => setMetaverseWalkthroughModalOpen(false)} />}
      {innovationLeadModalOpen && (
        <InnovationLeadModal
          open={innovationLeadModalOpen}
          closeModal={() => setinnovationLeadModalOpen(false)}
          leadFormPageLocation={getFormPageLocation}
        />
      )}
    </ModalsContext.Provider>
  );
};
export default ModalsContextProvider;
